/**
 * stream_socket_transport.ts
 *
 * JTT/T-1078 over WebSocket transport.
 * Media decoding is delegated to IPlayerAdapter (IoC) so this class
 * remains free of browser media APIs.
 *
 * Concrete adapter: HybridPlayerAdapter (livekit_player)
 *   - WebCodecs VideoDecoder (H.264 + H.265 hardware) primary
 *   - h265web.js Wasm fallback for H.265 when hardware decode unavailable
 */

import type { IPlayerAdapter, PlayerAdapterStats, PlayerFrameQualityChange } from './player_adapter';

export type { IPlayerAdapter, PlayerAdapterStats, PlayerFrameQualityChange } from './player_adapter';
export type TrackHandler = (stream: MediaStream) => void;
export type FrameQualityHandler = (change: PlayerFrameQualityChange) => void;

export interface StreamSocketMetrics {
  bufferOccupancyMs?: number;
  videoBitrateKbps?: number;
  decodedFrames?: number;
  droppedFrames?: number;
  bytesReceived?: number;
  frameWidth?: number;
  frameHeight?: number;
  framesPerSecond?: number;
  framesDropped?: number;
  decoderImplementation?: string;
  streamCodec?: string;
  decoderCodec?: string;
}

export class StreamSocketTransport {
  private adapter: IPlayerAdapter | null;
  private trackHandler: TrackHandler | null = null;
  private errorHandler: ((err: Error) => void) | null = null;
  private frameQualityHandler: FrameQualityHandler | null = null;
  private connected = false;

  /**
   * @param adapter  Concrete player adapter. Pass `null` to keep as stub
   *                 (connect() is a no-op until an adapter is provided).
   */
  constructor(adapter: IPlayerAdapter | null = null) {
    this.adapter = adapter;
  }

  /** Connect to the relay WebSocket endpoint provided by session_bootstrap. */
  connect(uri: string): void {
    if (!this.adapter) {
      console.warn('[StreamSocketTransport] no adapter — stub mode');
      return;
    }

    // Rewrite ws://host:443/... → wss://host/... (signaling server may advertise
    // wrong scheme when relay sits behind a TLS reverse proxy on port 443).
    const parsed = new URL(uri);
    if (parsed.protocol === 'ws:' && parsed.port === '443') {
      parsed.protocol = 'wss:';
      parsed.port = '';
      uri = parsed.toString();
      console.info('[StreamSocketTransport] rewritten relay URI:', uri);
    }

    this.adapter.attach(
      uri,
      (stream) => {
        this.connected = true;
        this.trackHandler?.(stream);
      },
      (err) => {
        this.connected = false;
        console.error('[StreamSocketTransport]', err);
        this.errorHandler?.(err);
      },
      (change) => {
        this.frameQualityHandler?.(change);
      },
    );
    this.connected = true;
  }

  onTrack(handler: TrackHandler): void {
    this.trackHandler = handler;
  }

  onError(handler: (err: Error) => void): void {
    this.errorHandler = handler;
  }

  /** Subscribe to frame-quality state changes from the adapter's gate. */
  onFrameQuality(handler: FrameQualityHandler): void {
    this.frameQualityHandler = handler;
  }

  getMetrics(): StreamSocketMetrics {
    const stats: PlayerAdapterStats = this.adapter?.getStats() ?? {};
    return {
      bufferOccupancyMs: stats.bufferOccupancyMs,
      videoBitrateKbps: stats.videoBitrateKbps,
      decodedFrames: stats.decodedFrames,
      droppedFrames: stats.droppedFrames,
      bytesReceived: stats.bytesReceived,
      frameWidth: stats.frameWidth,
      frameHeight: stats.frameHeight,
      framesPerSecond: stats.framesPerSecond,
      framesDropped: stats.framesDropped,
      decoderImplementation: stats.decoderImplementation,
      streamCodec: stats.streamCodec,
      decoderCodec: stats.decoderCodec,
    };
  }

  close(): void {
    this.adapter?.detach();
    this.connected = false;
  }

  get isConnected(): boolean {
    return this.connected;
  }
}
